import type { GeoActivityCell, GeoLayer, GeoZoomTier } from '@pulsify/shared/dto'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useEffect, useMemo, useRef } from 'react'
import { env } from '~/env'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'

/**
 * CARTO's Dark Matter, loaded as a vector style. Their raster endpoint started answering with
 * an "API KEY REQUIRED" watermark tile in 2026, but the vector tiles behind this style are
 * still served without one - and a vector style is what lets the basemap be tuned per layer
 * below, which a flat raster image never could.
 */
const BASEMAP = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json'

/**
 * Pushes the basemap back so the activity is the only thing with full contrast: roads and
 * borders thin out, and place labels stay readable without competing with the data.
 */
function mutedBasemap(map: maplibregl.Map): void {
  for (const layer of map.getStyle()?.layers ?? []) {
    if (layer.type === 'line') {
      map.setPaintProperty(layer.id, 'line-opacity', 0.45)
    }
    if (layer.type === 'symbol' && 'layout' in layer && layer.layout?.['text-field']) {
      map.setPaintProperty(layer.id, 'text-opacity', 0.55)
    }
  }
}

const SOURCE_ID = 'activity-cells'
const GLOW_LAYER = 'activity-glow'
const HEAT_LAYER = 'activity-heat'
const CORE_LAYER = 'activity-core'

/** Austria, which is where the map opens; Europe is one pinch away. */
const AUSTRIA_BOUNDS: [number, number, number, number] = [9.5, 46.3, 17.2, 49.1]

const TIER_FOR_ZOOM = (zoom: number): GeoZoomTier =>
  zoom < 6 ? 'europe' : zoom < 9 ? 'country' : 'city'

function metricFor(cell: GeoActivityCell, layer: GeoLayer): number {
  if (layer === 'scans') return cell.scans
  if (layer === 'live') return cell.live
  return cell.people
}

function token(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

function toFeatureCollection(
  cells: readonly GeoActivityCell[],
  layer: GeoLayer,
): GeoJSON.FeatureCollection {
  const values = cells.map((cell) => metricFor(cell, layer))
  const max = Math.max(1, ...values)
  return {
    type: 'FeatureCollection',
    features: cells.map((cell, index) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [cell.lng, cell.lat] },
      properties: {
        weight: values[index] / max,
        value: values[index],
        people: cell.people,
        opens: cell.opens,
        scans: cell.scans,
        live: cell.live,
      },
    })),
  }
}

interface GeoMapProps {
  cells: readonly GeoActivityCell[]
  layer: GeoLayer
  /** Discrete cells, or a continuous density surface that blurs where any one of them sits. */
  style: 'dots' | 'heat'
  onTierChange: (tier: GeoZoomTier) => void
}

export function GeoMap({ cells, layer, style, onTierChange }: GeoMapProps) {
  const container = useRef<HTMLDivElement>(null)
  const map = useRef<maplibregl.Map | null>(null)
  const popup = useRef<maplibregl.Popup | null>(null)
  const tier = useRef<GeoZoomTier>('country')
  const notifyTier = useRef(onTierChange)
  notifyTier.current = onTierChange

  const data = useMemo(() => toFeatureCollection(cells, layer), [cells, layer])
  const latestData = useRef(data)
  latestData.current = data
  const locale = getLocale()
  const localeRef = useRef(locale)
  localeRef.current = locale

  useEffect(() => {
    if (!container.current || map.current) return
    const instance = new maplibregl.Map({
      container: container.current,
      style: BASEMAP,
      bounds: AUSTRIA_BOUNDS,
      fitBoundsOptions: { padding: 32 },
      attributionControl: { compact: true },
      maxZoom: 13,
      minZoom: 3,
      // Attributes basemap traffic to our CARTO account and keeps working if they start
      // requiring a key on the vector tiles as they already do on the raster ones.
      transformRequest: (url) =>
        env.VITE_CARTO_BASEMAP_KEY && url.includes('cartocdn.com')
          ? { url: `${url}${url.includes('?') ? '&' : '?'}api_key=${env.VITE_CARTO_BASEMAP_KEY}` }
          : { url },
    })
    instance.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    instance.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left')

    const accent = token('--brand', '#ff5a3c')
    const accentStrong = token('--brand-strong', '#e14a2c')

    instance.on('load', () => {
      mutedBasemap(instance)
      instance.addSource(SOURCE_ID, { type: 'geojson', data: latestData.current })
      instance.addLayer({
        id: GLOW_LAYER,
        type: 'circle',
        source: SOURCE_ID,
        paint: {
          'circle-color': accent,
          'circle-blur': 1,
          'circle-opacity': 0.22,
          'circle-radius': ['interpolate', ['linear'], ['get', 'weight'], 0, 6, 1, 24],
        },
      })
      instance.addLayer({
        id: HEAT_LAYER,
        type: 'heatmap',
        source: SOURCE_ID,
        layout: { visibility: 'none' },
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'weight'], 0, 0.15, 1, 1],
          'heatmap-intensity': 1.1,
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 3, 18, 13, 55],
          'heatmap-opacity': 0.8,
          // Cold to hot, so density reads as temperature: quiet areas sit blue and the
          // busiest run through yellow into red.
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0,
            'rgba(12,28,82,0)',
            0.15,
            'rgb(32,86,214)',
            0.35,
            'rgb(0,186,214)',
            0.55,
            'rgb(74,214,112)',
            0.72,
            'rgb(244,214,64)',
            0.88,
            'rgb(248,140,38)',
            1,
            'rgb(226,42,38)',
          ],
        },
      })
      instance.addLayer({
        id: CORE_LAYER,
        type: 'circle',
        source: SOURCE_ID,
        paint: {
          'circle-color': accentStrong,
          'circle-opacity': 0.95,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1,
          'circle-stroke-opacity': 0.35,
          // Small enough to read as a precise marker rather than a blob, and it grows with
          // zoom so close-up cells do not stay pinpricks.
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            4,
            ['interpolate', ['linear'], ['get', 'weight'], 0, 2.5, 1, 6],
            13,
            ['interpolate', ['linear'], ['get', 'weight'], 0, 5, 1, 11],
          ],
        },
      })
    })

    const reportTier = () => {
      const next = TIER_FOR_ZOOM(instance.getZoom())
      if (next !== tier.current) {
        tier.current = next
        notifyTier.current(next)
      }
    }
    instance.on('zoomend', reportTier)

    popup.current = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'pulsify-geo-popup',
    })
    instance.on('mousemove', CORE_LAYER, (event) => {
      const feature = event.features?.[0]
      if (!feature || !popup.current) return
      instance.getCanvas().style.cursor = 'pointer'
      const props = feature.properties as Record<string, number>
      const number = (value: number) => new Intl.NumberFormat(localeRef.current).format(value ?? 0)
      popup.current
        .setLngLat(event.lngLat)
        .setText(
          m.geoCellTooltip({
            people: number(props.people),
            opens: number(props.opens),
            scans: number(props.scans),
          }),
        )
        .addTo(instance)
    })
    instance.on('mouseleave', CORE_LAYER, () => {
      instance.getCanvas().style.cursor = ''
      popup.current?.remove()
    })

    map.current = instance
    return () => {
      popup.current?.remove()
      instance.remove()
      map.current = null
    }
    // Built once: later cell updates go through setData below, and the tier and locale are
    // read from refs, so neither re-creates the map.
  }, [])

  useEffect(() => {
    const instance = map.current
    if (!instance) return
    const source = instance.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined
    if (source) source.setData(data)
  }, [data])

  useEffect(() => {
    const instance = map.current
    if (!instance) return
    const apply = () => {
      if (!instance.getLayer(HEAT_LAYER)) return
      const heat = style === 'heat'
      instance.setLayoutProperty(HEAT_LAYER, 'visibility', heat ? 'visible' : 'none')
      for (const id of [GLOW_LAYER, CORE_LAYER]) {
        instance.setLayoutProperty(id, 'visibility', heat ? 'none' : 'visible')
      }
    }
    if (instance.isStyleLoaded()) apply()
    else instance.once('idle', apply)
  }, [style])

  return <div ref={container} className="w-full h-full" />
}
